import { useEffect, useMemo } from 'react';

import { UseFormReturn, useFormState } from 'react-hook-form';

import {
  getClosedNoNewBusinessValue,
  getPastDueStatus,
  type PastDueOwner,
} from '@/hooks/usePastDueStatus';
import { EmbryoFormData } from '@/pages/embryo-app/schema';
import { Account } from '@/rtk-query.gql';
import { EmbryoDetailDto } from '@/services/embryo-service';
import {
  parseWallClock,
  toWallClockISO,
  transformCheckboxValue,
} from '@/utils';

import { EMBRYO_STATUS, IMPLANTABLE_STATUSES } from './constants';
import { useEmbryoFieldsEnabled } from './full-inventory';
import { dateFields } from './tabs';
import {
  validateBulkImplantStatuses,
  validateImplantDate,
  validateReversalFields,
  validateStatusChange,
} from './utils';

export type FormMessage = {
  severity: 'success' | 'info' | 'warn' | 'error';
  summary: string;
  detail: string;
};

export const IMPLANT_REQUIRED_FIELDS = [
  'calfType',
  'implantDate',
  'clientRecipient',
  'clinicRanchHaulIn',
  'siteId',
] as const;

export function resolveEmbryoFormData(embryo: EmbryoDetailDto) {
  const resolvedStatus = embryo.status;

  const donor =
    embryo.donor ??
    (embryo.donorId
      ? { id: embryo.donorId, name: embryo.donorName ?? '' }
      : undefined);

  const sire =
    embryo.sire ??
    (embryo.sireId
      ? { id: embryo.sireId, name: embryo.sireName ?? '' }
      : undefined);

  const semenId = embryo.semenId
    ? {
        id: embryo.semenId,
        name: (embryo as any).semenName ?? (embryo as any).naabCode ?? '',
      }
    : undefined;

  const breedId = (embryo as any).breedId
    ? {
        id: (embryo as any).breedId,
        name: (embryo as any).breedName ?? '',
      }
    : undefined;

  const probableDonorId = embryo.probableDonorId
    ? {
        id: embryo.probableDonorId,
        name: (embryo as any).probableDonorName ?? '',
      }
    : undefined;

  return {
    ...embryo,
    status: resolvedStatus,
    donor,
    sire,
    semenId,
    breedId,
    probableDonorId,
  };
}

const CHECKBOX_FIELDS = [
  'gnrhUsedAtImplant',
  'lowQuality',
  'additionalEmbryoImplant',
  'dtPlus',
  'tt',
] as const;

function normalizeForSubmit(data: Partial<EmbryoFormData>) {
  const extractId = (v: any) =>
    v != null && typeof v === 'object' && 'id' in v ? v.id : v;

  const out: Record<string, any> = {};

  // unwraps {id, name} objects to ids
  if (data.donor != null) out.donorId = extractId(data.donor);
  if (data.sire != null) out.sireId = extractId(data.sire);
  if (data.semenId != null) out.semenId = extractId(data.semenId);
  if (data.breedId != null) out.breedId = extractId(data.breedId);
  const pd = data.probableDonorId as any;
  if (pd != null) out.probableDonorId = extractId(pd) || null;
  if (data.donorAge != null) out.donorAge = String(data.donorAge);
  if (data.preBook !== undefined) {
    out.preBook = data.preBook == null ? null : data.preBook ? 1 : 0;
  }
  if (data.storageLocationId === null) {
    out.storageLocationId = null;
  }
  if (data.abort !== undefined) {
    out.abort = data.abort == null ? null : data.abort ? 1 : 0;
  }

  // date fields convert Date objects to ISO strings
  for (const field of dateFields) {
    const v = (data as any)[field];
    if (v !== undefined) {
      out[field] = v instanceof Date ? toWallClockISO(v) : v;
    }
  }

  // checkbox fields convert bool/num/null
  for (const field of CHECKBOX_FIELDS) {
    const v = (data as any)[field];
    if (v !== undefined) {
      out[field] = transformCheckboxValue(v);
    }
  }

  return out;
}

interface UseEmbryoFormValidationArgs {
  methods: UseFormReturn<any>;
  currentStatuses: number[];
  isBulkMode: boolean;
  selectedEmbryos?: Array<{ status?: number }>;
  originalStateCode?: number;
  isOutsideEmbryo?: boolean;
  accountDataMap?: Map<string, Account>;
  originalOwnerId?: string;
}

export function useEmbryoFormValidation({
  methods,
  currentStatuses,
  isBulkMode,
  selectedEmbryos = [],
  originalStateCode,
  isOutsideEmbryo = false,
  accountDataMap,
  originalOwnerId,
}: UseEmbryoFormValidationArgs) {
  const { trigger, clearErrors, setError } = methods;
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();

  const watchedStatusCode = methods.watch('status');
  const watchedImplantDate = methods.watch('implantDate');
  const watchedImplantType = methods.watch('calfType');
  const watchedSiteId = methods.watch('siteId');
  const watchedClinicRanch = methods.watch('clinicRanchHaulIn');
  const watchedClientRecipient = methods.watch('clientRecipient');
  const watchedStorageLocationId = methods.watch('storageLocationId');
  const watchedStateCode = methods.watch('stateCode');
  const watchedStatusComment = methods.watch('statusComment');
  const watchedSoldTo = methods.watch('soldTo');
  const watchedTransComment = methods.watch('transComment');
  const watchedTransferDate = methods.watch('transferDate');
  const watchedOwnerId = methods.watch('ownerId');

  const isImplantTransition =
    !isOutsideEmbryo &&
    watchedStatusCode === EMBRYO_STATUS.IMPLANTED &&
    currentStatuses.some((s) => s !== EMBRYO_STATUS.IMPLANTED);

  useEffect(() => {
    if (isImplantTransition) {
      trigger(IMPLANT_REQUIRED_FIELDS as unknown as any);
    } else {
      clearErrors(IMPLANT_REQUIRED_FIELDS as unknown as any);
    }
  }, [isImplantTransition, trigger, clearErrors]);

  const isReversal =
    !isOutsideEmbryo &&
    watchedStatusCode !== undefined &&
    (IMPLANTABLE_STATUSES as readonly number[]).includes(
      watchedStatusCode as number,
    ) &&
    currentStatuses.some((s) => s === EMBRYO_STATUS.IMPLANTED);

  const bulkImplantValidation =
    isBulkMode && isImplantTransition
      ? validateBulkImplantStatuses(selectedEmbryos)
      : { valid: true };

  const implantRequiredMissing =
    isImplantTransition &&
    (!watchedImplantDate ||
      !watchedImplantType ||
      !watchedSiteId ||
      !watchedClinicRanch ||
      (!addRemoveEmbryoFields && !watchedClientRecipient));

  const implantDateInFuture =
    isImplantTransition &&
    !!watchedImplantDate &&
    parseWallClock(watchedImplantDate)! > new Date();

  const reversalFieldsMissing =
    isReversal &&
    !validateReversalFields(watchedStorageLocationId, watchedSiteId).valid;

  const stateCodeChanged =
    originalStateCode !== undefined &&
    watchedStateCode !== undefined &&
    watchedStateCode !== originalStateCode;

  const commentRequiredMissing =
    !isOutsideEmbryo &&
    stateCodeChanged &&
    (!watchedStatusComment || !watchedStatusComment.trim());

  const hasSoldTo = !!watchedSoldTo && !!watchedSoldTo.trim();
  const transactionFieldsMissing =
    !addRemoveEmbryoFields &&
    hasSoldTo &&
    (!watchedTransComment ||
      !watchedTransComment.trim() ||
      !watchedTransferDate);

  useEffect(() => {
    if (commentRequiredMissing) {
      setError('statusComment', {
        type: 'custom',
        message: 'Comment is required when changing status',
      });
    } else {
      clearErrors('statusComment');
    }
  }, [commentRequiredMissing, setError, clearErrors]);

  useEffect(() => {
    if (addRemoveEmbryoFields) {
      clearErrors('transComment');
      clearErrors('transferDate');
      return;
    }
    if (hasSoldTo && (!watchedTransComment || !watchedTransComment.trim())) {
      setError('transComment', {
        type: 'custom',
        message: 'Transaction Comment is required when Sold To is filled',
      });
    } else {
      clearErrors('transComment');
    }
    if (hasSoldTo && !watchedTransferDate) {
      setError('transferDate', {
        type: 'custom',
        message: 'Transaction Date is required when Sold To is filled',
      });
    } else {
      clearErrors('transferDate');
    }
  }, [
    addRemoveEmbryoFields,
    hasSoldTo,
    watchedTransComment,
    watchedTransferDate,
    setError,
    clearErrors,
  ]);

  const ownerOrSoldToMissing =
    !isBulkMode && !watchedOwnerId && !watchedSoldTo?.trim();

  const selectedAccount = watchedOwnerId
    ? (accountDataMap?.get(watchedOwnerId) ?? null)
    : null;

  const closedNoNewBusinessValue = useMemo(getClosedNoNewBusinessValue, []);

  const ownerPastDueResult = useMemo(() => {
    if (!selectedAccount || watchedOwnerId === originalOwnerId) return null;
    const adapted: PastDueOwner = {
      aging60: (selectedAccount.aging60 ?? 0) > 0,
      aging180:
        (selectedAccount.aging120 ?? 0) > 0 ||
        (selectedAccount.aging180 ?? 0) > 0,
      aging365: (selectedAccount.aging365 ?? 0) > 0,
      accountStatus: selectedAccount.accountStatus ?? null,
      tog90daysPastDue: selectedAccount.tog90daysPastDue ?? null,
      tog90DayOverrideExpireDate:
        selectedAccount.tog90DayOverrideExpireDate ?? null,
    };
    return getPastDueStatus(adapted, [], closedNoNewBusinessValue);
  }, [
    selectedAccount,
    watchedOwnerId,
    originalOwnerId,
    closedNoNewBusinessValue,
  ]);

  const ownerPastDueBlocked = ownerPastDueResult?.isLocked ?? false;

  const implantDateValidation =
    watchedStatusCode === EMBRYO_STATUS.IMPLANTED
      ? validateImplantDate(watchedImplantDate)
      : { valid: true };

  const { dirtyFields } = useFormState({
    control: methods.control,
    name: ['status'] as const,
  });
  const isStatusDirty = !!dirtyFields.status;

  const statusMessages: FormMessage[] = useMemo(() => {
    const msgs: FormMessage[] = [];

    if (
      !isOutsideEmbryo &&
      isStatusDirty &&
      watchedStatusCode !== undefined &&
      currentStatuses.length > 0
    ) {
      const isImplantSelected = watchedStatusCode === EMBRYO_STATUS.IMPLANTED;
      const isFrozenOrShippedSelected = (
        IMPLANTABLE_STATUSES as readonly number[]
      ).includes(watchedStatusCode as number);

      if (isImplantSelected) {
        if (currentStatuses.length > 1) {
          const ineligibleCount = currentStatuses.filter(
            (s) => !(IMPLANTABLE_STATUSES as readonly number[]).includes(s),
          ).length;
          if (ineligibleCount > 0) {
            msgs.push({
              severity: 'error',
              summary: 'Ineligible Embryos',
              detail: `${ineligibleCount} of ${currentStatuses.length} embryo(s) are not Frozen or Shipped and cannot be marked as Implanted. All embryos must be eligible before saving.`,
            });
          } else {
            msgs.push({
              severity: 'warn',
              summary: 'Implant Status Selected',
              detail:
                'All selected embryos will be marked as Implanted and removed from frozen inventory.',
            });
          }
        } else {
          const currentStatus = currentStatuses[0];
          const validation = validateStatusChange(
            currentStatus,
            watchedStatusCode,
          );
          if (!validation.valid) {
            msgs.push({
              severity: 'error',
              summary: 'Invalid Status Change',
              detail: validation.message ?? 'Status change not allowed.',
            });
          } else {
            msgs.push({
              severity: 'warn',
              summary: 'Implant Status Selected',
              detail:
                'Status set to Implanted — please complete all required fields on the Implant tab before saving.',
            });
          }
        }
      } else if (isFrozenOrShippedSelected) {
        if (
          currentStatuses.length === 1 &&
          currentStatuses[0] === EMBRYO_STATUS.IMPLANTED
        ) {
          const validation = validateStatusChange(
            currentStatuses[0],
            watchedStatusCode,
          );
          if (!validation.valid) {
            msgs.push({
              severity: 'error',
              summary: 'Invalid Status Change',
              detail: validation.message ?? 'Status change not allowed.',
            });
          } else {
            msgs.push({
              severity: 'warn',
              summary: 'Returning to Inventory',
              detail: reversalFieldsMissing
                ? 'This embryo is being reversed from Implanted — storage location and site are required.'
                : 'This embryo is being reversed from Implanted.',
            });
          }
        }

        if (currentStatuses.length > 1) {
          const implantedCount = currentStatuses.filter(
            (s) => s === EMBRYO_STATUS.IMPLANTED,
          ).length;
          if (implantedCount > 0) {
            msgs.push({
              severity: 'warn',
              summary: 'Returning to Inventory',
              detail: reversalFieldsMissing
                ? implantedCount === currentStatuses.length
                  ? `All ${implantedCount} embryo(s) will be reversed from Implanted — storage location and site are required.`
                  : `${implantedCount} of ${currentStatuses.length} embryo(s) will be reversed from Implanted — storage location and site are required.`
                : implantedCount === currentStatuses.length
                  ? `All ${implantedCount} embryo(s) will be reversed from Implanted.`
                  : `${implantedCount} of ${currentStatuses.length} embryo(s) will be reversed from Implanted.`,
            });
          }
        }
      }
    }

    if (!implantDateValidation.valid && implantDateValidation.message) {
      msgs.push({
        severity: 'error',
        summary: 'Invalid Implant Date',
        detail: implantDateValidation.message,
      });
    }

    if (ownerPastDueBlocked) {
      msgs.push({
        severity: 'error',
        summary: 'Transfer Restricted',
        detail: 'This account has an outstanding balance past 90 days.',
      });
    } else if (
      ownerPastDueResult?.hasOver90Days &&
      !ownerPastDueResult.isLocked
    ) {
      msgs.push({
        severity: 'warn',
        summary: 'Past Due Override Active',
        detail: ownerPastDueResult.overrideActiveUntil
          ? `This account is past due but has an active override until ${ownerPastDueResult.overrideActiveUntil}.`
          : 'This account is past due but has an active override.',
      });
    }

    if (ownerOrSoldToMissing && !isOutsideEmbryo) {
      msgs.push({
        severity: 'warn',
        summary: 'Owner Required',
        detail:
          'An embryo owner must be selected, or the Sold To field must be filled.',
      });
    }

    return msgs;
  }, [
    isOutsideEmbryo,
    isStatusDirty,
    watchedStatusCode,
    currentStatuses,
    reversalFieldsMissing,
    implantDateValidation.valid,
    implantDateValidation.message,
    ownerPastDueBlocked,
    ownerPastDueResult,
    ownerOrSoldToMissing,
  ]);

  const isSaveDisabled =
    !bulkImplantValidation.valid ||
    implantRequiredMissing ||
    implantDateInFuture ||
    reversalFieldsMissing ||
    commentRequiredMissing ||
    transactionFieldsMissing ||
    (!isOutsideEmbryo && ownerOrSoldToMissing) ||
    ownerPastDueBlocked;

  return {
    isImplantTransition,
    isReversal,
    bulkImplantValidation,
    implantRequiredMissing,
    implantDateInFuture,
    reversalFieldsMissing,
    commentRequiredMissing,
    transactionFieldsMissing,
    ownerOrSoldToMissing,
    ownerPastDueBlocked,
    isSaveDisabled,
    normalizeForSubmit,
    statusMessages,
  };
}
